import React from 'react'
import classNames from 'classnames'
import {BsChevronRight} from 'react-icons/bs'
import {DirectoryTypeKeys, directoryTypes} from '../consts/directoryTypes'
import getTitle from "../../../../helpers/getTitle";

// org_type (Organization) -> key trong directoryTypes — Giáo xứ/Giáo phận/Dòng tu đều có
// thể có 1 Organization đi kèm (org_type 300/302/400), Hội đoàn hoàn toàn là Organization (999).
const ORG_TYPE_TO_DIRECTORY_KEY = {
    300: DirectoryTypeKeys.PARISH,
    302: DirectoryTypeKeys.DIOCESE,
    400: DirectoryTypeKeys.RELIGIOUS_ORDER,
    999: DirectoryTypeKeys.ORGANIZATION,
}

const DirectoryCardRow = ({item, typeConfig, t, onSelect, renderContextMenu}) => {
    if (!typeConfig || !item) return null

    const {icon: Icon, colorClass, badgeBg, label, getBadge, getAddress, getAvatar, getLabel} = typeConfig
    // getLabel - nhãn badge màu theo TỪNG item thay vì cố định cả typeConfig (vd Dòng tu gộp chung 1
    // typeConfig cho cả 4 bản chất Dòng tu/Tu hội đời/Tu đoàn tông đồ/CGTH - trước đây card luôn hiện
    // "DÒNG TU" dù item thật là CGTH, bạn báo thiếu phân biệt). Trả về KEY i18n (giống `label` gốc),
    // t() ở dưới lo dịch - không trả text đã dịch sẵn để nhất quán cách typeConfig.label hoạt động.
    const displayLabel = getLabel?.(item) ?? label
    const badge = getBadge?.(item, t)
    const address = getAddress?.(item)
    // Field ảnh khác tên theo từng loại (avatar/photo/owner.avatar...) - typeConfig tự biết đọc
    // đúng chỗ, item?.avatar chỉ là fallback chung cho type nào chưa khai báo getAvatar riêng.
    const avatar = getAvatar?.(item) ?? item?.avatar
    const title = getTitle(item) || item?.organization_name
    // renderContextMenu có thể tự trả null theo quyền từng dòng (vd item?.edit) - không render
    // vùng tuyệt đối + không chặn click khi không có gì để hiện, tránh vùng chết vô hình trên card.
    const contextMenu = renderContextMenu?.(item)

    return (
        <div
            onClick={() => onSelect?.(item, typeConfig)}
            className="flex items-center gap-3 px-3 py-2.5 background rounded-xl border border-color-50 hover:shadow-md cursor-pointer transition-all relative">
            <div
                style={{width: 44, height: 44}}
                className={classNames('flex flex-center flex-shrink-0 rounded-xl overflow-hidden', badgeBg)}>
                {avatar
                    ? <img src={avatar} alt={title} className="w-full h-full object-cover"/>
                    : <Icon size={20} className={colorClass}/>}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={classNames('text-2xs font-bold uppercase tracking-wider px-1.5 py-0.5 rounded', badgeBg)}>
                        {t(displayLabel)}
                    </span>
                    {badge && (
                        <span className="text-2xs text-color-400 font-medium truncate">
                            {badge}
                        </span>
                    )}
                </div>
                <div className={classNames('font-semibold text-sm text-color-000 leading-snug truncate mt-0.5', contextMenu && 'mr-6')}>
                    {title}
                </div>
                {address && (
                    <div className="text-xs text-color-300 truncate mt-0.5">
                        {address}
                    </div>
                )}
            </div>
            <div
                className="absolute flex gap-2 items-center justify-center right-5 h-full">
                {contextMenu ? (
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="contextIcon">
                        {contextMenu}
                    </div>
                ) : (
                    <BsChevronRight size={14} className="text-color-400 flex-shrink-0"/>
                )}
            </div>
        </div>
    )
}

// Dùng cho tab theo từng loại cụ thể — typeConfig cố định cho cả danh sách, item là owner gốc.
export const renderDirectoryCard = ({typeConfig, translate: t, onSelect, renderContextMenu} = {}) => (item, index) => (
    <DirectoryCardRow key={item?.id ?? index} item={item} typeConfig={typeConfig} t={t} onSelect={onSelect} renderContextMenu={renderContextMenu}/>
)

// Dùng cho tab "Tất cả" — item có dạng EntityLookupTransformer (owner_type/owner ở ngoài, field thật nằm trong owner).
export const renderDirectoryAllCard = ({translate: t, onSelect} = {}) => (item, index) => {
    const owner = item?.owner
    if (!owner) return null

    // else if (item.owner_type === 'organization') {
    //     const key = ORG_TYPE_TO_DIRECTORY_KEY[Number(owner?.org_type)] ?? DirectoryTypeKeys.CHURCH
    //     typeConfig = directoryTypes.find((e) => e.key === key)
    // }
    // religious_order gộp chung 1 typeConfig cho cả 4 bản chất (Dòng tu/Tu hội đời/Tu đoàn tông
    // đồ/CGTH) - cùng icon/màu nền, badge nhãn tự phân biệt theo item.nature_typecd qua
    // typeConfig.getLabel (xem directoryTypes.js + DirectoryCardRow).
    const typeConfig = directoryTypes.find((e) => e.owner_type === item.owner_type)
    if (!typeConfig) return null

    return <DirectoryCardRow key={item?.id ?? index} item={owner} typeConfig={typeConfig} t={t} onSelect={onSelect}/>
}

// Dùng cho "Tổ chức của tôi" (auth/organization/gets) — item là Organization gốc (org_type số:
// 300 Giáo xứ/302 Giáo phận/400 Dòng tu/999 Hội đoàn-Cộng đoàn), không có owner_type như trên nên
// map qua ORG_TYPE_TO_DIRECTORY_KEY để lấy đúng icon/màu/nhãn phân biệt loại tổ chức.
export const renderMyOrganizationCard = ({translate: t, onSelect} = {}) => (item, index) => {
    if (!item) return null
    const key = ORG_TYPE_TO_DIRECTORY_KEY[Number(item.org_type)] ?? DirectoryTypeKeys.ORGANIZATION
    const typeConfig = directoryTypes.find((e) => e.key === key)
    if (!typeConfig) return null

    return <DirectoryCardRow key={item?.id ?? index} item={item} typeConfig={typeConfig} t={t} onSelect={onSelect}/>
}
